import { FC, ReactNode, useEffect, useRef, useSyncExternalStore } from "react";
import * as S from "./styles";
import { SVG } from "../SVG";
import { faClose } from "@fortawesome/pro-light-svg-icons";
import { Portal } from "~/helpers/portal";
import { useModalSearchParams } from "~/hooks/useModalSearchParams";
import {
  getTopModal,
  pushModal,
  removeModal,
  subscribeToModalStack,
} from "./modalStack";

export interface IModalProps {
  isShowing: boolean;
  hide?: () => void;
  children: ReactNode;
  padding?: string;
  minWidth?: string | number;
  maxWidth?: string | number;
  hideCloseButton?: boolean;
  maxHeight?: string;
  fixedElement?: ReactNode;
  id?: string;
  width?: number | string;
  /** When false, overlay clicks do not dismiss (no hide / URL update). Default true. */
  closeOnBackdropClick?: boolean;
  scrollable?: "auto" | "hidden";
}

const Modal: FC<IModalProps> = ({
  isShowing,
  hide,
  children,
  padding,
  minWidth,
  maxHeight,
  hideCloseButton,
  fixedElement,
  id,
  maxWidth,
  width,
  closeOnBackdropClick = true,
  scrollable = "auto",
}) => {
  // Identity for this instance in the global stack. A ref keeps it stable across
  // re-renders so the modal doesn't re-register on every render.
  const tokenRef = useRef<symbol>();
  if (!tokenRef.current) tokenRef.current = Symbol("modal");
  const token = tokenRef.current;

  const topModal = useSyncExternalStore(
    subscribeToModalStack,
    getTopModal,
    getTopModal
  );

  useEffect(() => {
    if (isShowing) {
      pushModal(token);
    } else {
      removeModal(token);
    }
  }, [isShowing, token]);

  // Also drop out of the stack if this modal unmounts while still open,
  // otherwise it would block every other modal forever.
  useEffect(() => () => removeModal(token), [token]);

  // Only the most recently opened modal is visible. Others stay mounted but
  // hidden, and reappear when the one above them closes.
  const isSuppressed =
    isShowing && topModal !== undefined && topModal !== token;

  const handleSearchParamUpdate = useModalSearchParams(
    id,
    isShowing && !isSuppressed
  );
  const handleHide = () => {
    if (id) handleSearchParamUpdate(false, id);
    if (hide) {
      hide();
    }
  };

  if (!isShowing) return null;

  return (
    <Portal>
      <S.ModalWrapper
        className="modal-wrapper"
        style={isSuppressed ? { display: "none" } : undefined}
        aria-hidden={isSuppressed || undefined}
        onClick={closeOnBackdropClick ? handleHide : undefined}>
        <S.ModalContent
          className="modal-content"
          maxHeight={maxHeight}
          maxWidth={maxWidth}
          minWidth={minWidth ? minWidth : "fit-content"}
          width={width ? width : "auto"}
          onClick={(e) => e.stopPropagation()}
          scrollable={scrollable}>
          {hide && !hideCloseButton ? (
            <S.CloseContainer className="close-button" onClick={handleHide}>
              <SVG icon={faClose} size="xl" />
            </S.CloseContainer>
          ) : null}
          <S.ModalScrollWrapper padding={padding}>
            {children}
          </S.ModalScrollWrapper>
        </S.ModalContent>
      </S.ModalWrapper>

      {fixedElement && !isSuppressed ? (
        <S.FixedElement>{fixedElement}</S.FixedElement>
      ) : null}
    </Portal>
  );
};

export default Modal;
